import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { getApiUrl, isFeatureEnabled } from '../config/env';

import { QUERY_KEYS } from './useDashboardData';

interface RealTimeEvent {
  type: 'metrics_updated' | 'data_sync_completed' | 'export_completed';
  payload: any;
  timestamp: string;
}

interface UseRealTimeUpdatesOptions {
  enabled?: boolean;
  onEvent?: (event: RealTimeEvent) => void;
  onError?: (error: Event) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

/**
 * Hook for real-time data updates using Server-Sent Events (SSE)
 * Automatically invalidates relevant queries when data updates are received
 */
export function useRealTimeUpdates(options: UseRealTimeUpdatesOptions = {}) {
  const {
    enabled = isFeatureEnabled('VITE_ENABLE_REAL_TIME_UPDATES'),
    onEvent,
    onError,
    onConnect,
    onDisconnect,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const queryClient = useQueryClient();
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const connect = () => {
    // Early return if not enabled - don't even attempt connection
    if (!enabled) {
      return;
    }

    if (eventSourceRef.current) {
      return;
    }

    // Don't attempt to connect if we've exceeded max attempts
    if (reconnectAttempts.current >= maxReconnectAttempts) {
      setConnectionError('Max reconnection attempts reached');
      return;
    }

    try {
      const eventSource = new EventSource(`${getApiUrl()}/api/events/stream`, {
        withCredentials: true,
      });

      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;
        onConnect?.();
      };

      eventSource.onmessage = event => {
        try {
          const data: RealTimeEvent = JSON.parse(event.data);
          handleRealTimeEvent(data);
          onEvent?.(data);
        } catch (error) {
          console.error('Failed to parse real-time event:', error);
        }
      };

      eventSource.onerror = error => {
        setIsConnected(false);
        onError?.(error);

        // Close the connection immediately
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }

        // Only attempt to reconnect if we haven't exceeded max attempts
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(
            1000 * Math.pow(2, reconnectAttempts.current),
            30000
          );
          setConnectionError(
            `Connection failed. Retrying in ${Math.round(delay / 1000)}s...`
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        } else {
          setConnectionError('Real-time updates unavailable. Using mock data.');
          console.warn(
            'Real-time connection failed after max attempts. Continuing with mock data.'
          );
        }
      };

      // Handle specific event types
      eventSource.addEventListener('metrics_updated', event => {
        const data = JSON.parse(event.data);
        handleMetricsUpdate(data);
      });

      eventSource.addEventListener('data_sync_completed', event => {
        const data = JSON.parse(event.data);
        handleDataSyncCompleted(data);
      });

      eventSource.addEventListener('export_completed', event => {
        const data = JSON.parse(event.data);
        handleExportCompleted(data);
      });
    } catch (error) {
      console.warn(
        'Failed to establish real-time connection (backend may not be running):',
        error
      );
      setConnectionError('Backend unavailable');
      reconnectAttempts.current = maxReconnectAttempts; // Prevent further attempts
    }
  };

  const disconnect = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    setIsConnected(false);
    onDisconnect?.();
  };

  const handleRealTimeEvent = (event: RealTimeEvent) => {
    switch (event.type) {
      case 'metrics_updated':
        handleMetricsUpdate(event.payload);
        break;
      case 'data_sync_completed':
        handleDataSyncCompleted(event.payload);
        break;
      case 'export_completed':
        handleExportCompleted(event.payload);
        break;
      default:
        console.log('Unknown real-time event type:', event.type);
    }
  };

  const handleMetricsUpdate = (payload: any) => {
    const { dashboardType, filters } = payload;

    // Invalidate specific dashboard metrics based on the update
    switch (dashboardType) {
      case 'leadership':
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.LEADERSHIP_METRICS],
          exact: false,
        });
        break;
      case 'scrum-master':
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.SCRUM_MASTER_METRICS],
          exact: false,
        });
        break;
      case 'developer':
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.DEVELOPER_METRICS],
          exact: false,
        });
        break;
      case 'all':
        // Invalidate all dashboard metrics
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.LEADERSHIP_METRICS],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.SCRUM_MASTER_METRICS],
          exact: false,
        });
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.DEVELOPER_METRICS],
          exact: false,
        });
        break;
    }
  };

  const handleDataSyncCompleted = (payload: any) => {
    const { source, syncType } = payload;

    // Invalidate all metrics when data sync is completed
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.LEADERSHIP_METRICS],
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.SCRUM_MASTER_METRICS],
      exact: false,
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.DEVELOPER_METRICS],
      exact: false,
    });

    // Also invalidate organization data if it might have changed
    if (syncType === 'full' || source === 'ADO') {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAMS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROGRAMS] });
    }

    if (syncType === 'full' || source === 'GitHub') {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REPOSITORIES] });
    }
  };

  const handleExportCompleted = (payload: any) => {
    const { exportId, status } = payload;

    // Update the export status in the cache
    queryClient.setQueryData(
      [QUERY_KEYS.EXPORT_STATUS, exportId],
      (oldData: any) => ({
        ...oldData,
        status,
        completedAt: new Date().toISOString(),
        ...(status === 'completed' &&
          payload.downloadUrl && {
            downloadUrl: payload.downloadUrl,
          }),
        ...(status === 'failed' &&
          payload.error && {
            error: payload.error,
          }),
      })
    );
  };

  // Connect on mount and when enabled changes
  useEffect(() => {
    if (enabled) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  return {
    isConnected,
    connectionError,
    connect,
    disconnect,
    reconnect: () => {
      disconnect();
      setTimeout(connect, 100);
    },
  };
}

/**
 * Hook for subscribing to specific real-time events
 */
export function useRealTimeEvent(
  eventType: RealTimeEvent['type'],
  handler: (payload: any) => void,
  enabled: boolean = true
) {
  useRealTimeUpdates({
    enabled,
    onEvent: event => {
      if (event.type === eventType) {
        handler(event.payload);
      }
    },
  });
}
